<template>
  <dropCell
    v-model="pickerOpen"
    :disabled="disabled"
    :editable="editable"
    :manual-open="true"
    :suggest-height="257"
    :drop-width="260"
    :class="datePickerId"
  >
    <template #trigger="{isOpen}">
      <div
        ref="trigger"
        :class="[$style.cell_trigger, $style.center, !editable ? $style.not_allowed : '']"
        @click="triggerClick"
      >
        <span
          :class="[$style.text_wrap,
                   $style.text_center,
                   disabled ? $style.text_disabled : '',
                   crossout && (disabled || !isOpen) ? $style.crossout :'',
                   isOverdue ? $style.overdue :'',
          ]"
          :style="{opacity: isOpen ? 1 : cellOpecity}"
        >
          {{ formattedDate }}
        </span>
      </div>
    </template>
    <template #drop>
      <webix-ui
        ref="drop"
        class="custom_datepicker_clear"
        :config="datepickerConfig"
        @click.native="calendarClick"
      />
    </template>
  </dropCell>
</template>

<script>
import moment from 'moment';
import dateHelper from '../../../../helpers/dateFormats';
import constants from '$$helpers/constants';
import calendarModule from '../../../../components/calendar';
import CalendarWorker from '../../../../components/workers/CalendarWorker';
import dropCell from '../dropPopup.vue';

export default {
  name: 'CalendarCell',
  components: { dropCell },
  props: {
    item: { type: Object, required: true },
    property: { type: [String, Number], required: true },
    extra: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, required: false, default: false },
    editable: { type: Boolean, required: false, default: true },
    forCustom: { type: Boolean, required: false, default: false },
    value: { type: [Object, String], default: null },
    discolor: { type: Number, required: false, default: 1 },
    crossout: { type: Boolean, required: false, default: false },
    overdue: { type: Boolean, required: false, default: false },
    calendarConfig: { type: Object, required: true },
  },
  emits: [
    'onChange',
  ],
  data() {
    return {
      pickerOpen: false,
      isCalendarClick: false,
      settingSelected: false,
    };
  },

  computed: {
    cellOpecity() {
      const val = +this.discolor || 1;

      if (this.disabled && val !== 1) { return 0.8; }

      return val;
    },
    formattedDate() {
      const val = this.item[this.property];

      if (!val) return '';
      const date = new Date(val);
      const format = this.extra ? dateHelper.getDateFormatForGanttGrid() : dateHelper.getDateFormat();

      return webix.Date.dateToStr(format)(date);
    },

    datePickerId() {
      return `datePicker-${this.item.id}-${this.componentKey}`;
    },

    isOverdue() {
      return this.overdue && this.property === 'end_date';
    },

    componentKey() {
      const min = 1;
      const max = 100000000;
      const random = Math.floor(Math.random() * (max - min)) + min;

      return `${new Date().getTime()}-${random}`;
    },

    isShowTodayBtn() {
      const isWorkToday = !this.isBlockedDate(new Date());

      if (this.property === 'start_date') {
        return isWorkToday;
      }

      if (this.property === 'end_date') {
        const isTodayAfterStartDate = dateHelper.createDayStartedAt00(new Date(_.clone(this.item.start_date))).valueOf() < new Date().valueOf();

        return isTodayAfterStartDate && isWorkToday;
      }

      return true;
    },

    datepickerConfig() {
      const component = this;
      const date = this.value || null;
      const showTime = this.property === 'start_date' || this.property === 'end_date';
      const config = {
        view: 'calendar',
        css: 'webix_calendar',
        id: component.datePickerId,
        monthSelect: true,
        navigation: true,
        weekHeader: true,
        borderless: true,
        fillspace: true,
        height: 260,
        calendar: {
          height: 300,
          cellHeight: 24,
        },
        date,
        timepicker: showTime,
        icons: [{
          template() {
            if (component.forCustom) {
              return `
              <div class='d-flex justify-space-between align-center ' style="width:224px; margin-top:-6px">
                <span role='button' tabindex='0' class='webix_cal_icon_clear webix_cal_icon' >${component.item[component.property] ? webix.i18n.calendar.clear : ''}</span>
                <span role='button' tabindex='0' class='webix_cal_icon_today webix_cal_icon'> ${webix.i18n.calendar.today}</span>
              </div>
              `;
            }
            const todayBtn = `<span role='button' tabindex='0' class='webix_cal_icon_today webix_cal_icon' style="margin-top:-26px">${webix.i18n.calendar.today}</span>`;

            return component.isShowTodayBtn ? todayBtn : '';
          },
          on_click: {
            webix_cal_icon_today() {
              let dataForWorker;
              
              if (component.property === 'end_date') {
                dataForWorker = {
                  date: dateHelper.createDayStartedAt00(new Date(new Date().getTime() + 24 * 60 * 60 * 1000)),
                  dir: 'past',
                  task: this.item,
                };
              } else {
                dataForWorker = {
                  date: dateHelper.createDayStartedAt00(new Date()),
                  dir: 'future',
                  task: this.item,
                };
              }

              CalendarWorker.calculate(dataForWorker, 'getClosestWorkTime')
                .then(closestDate => {
                  this.setValue(closestDate);
                })
                .finally(() => {
                  this.callEvent('onTodaySet', [this.getSelectedDate()]);
                });
            },
            webix_cal_icon_clear() {
              this.setValue('');
              this.callEvent('onDateClear', []);
            },
          },
        }],
        on: {
          onChange(val) {
            !component.settingSelected && component.selectDate(val);
            component.settingSelected = false;
          },
        },
        weekNumber: false,
        blockDates: component.isBlockedDate,
        blockTime: component.isBlockedTime,
        minuteStep: 1,
      };

      if (date) config.date = date;

      return config;
    },

  },

  mounted() {
    document.addEventListener('click', this.documentClick);
    document.addEventListener('contextmenu', this.closeCalendar);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.documentClick);
    document.removeEventListener('contextmenu', this.closeCalendar);

    if (!webix.$$(this.popupId) || !webix.$$(this.popupId).destructor) { return; }

    webix.$$(this.popupId).destructor();
  },

  methods: {
    calendarClick(e) {
      this.isCalendarClick = true;
    },
    documentClick(e) {
      const isTrigger = this.$refs.trigger ? this.$refs.trigger.contains(e.target) : false;
      const isDrop = this.isCalendarClick;

      if (!isDrop && !isTrigger) {
        this.pickerOpen = false;
      }
      this.isCalendarClick = false;
    },
    closeCalendar() {
      this.pickerOpen = false;
    },
    selectDate(val) {
      const value = val ? val[0] : '';

      if ((new Date(value)).getTime() === (new Date(this.value)).getTime()) { return; }

      if (this?.item.start_date && this.property === 'end_date' && ((new Date(value)).getTime() === (new Date(this?.item.start_date)).getTime())) {
        this.pickerOpen = false;

        return;
      }

      if (this.forCustom) {
        value.setHours(23, 59, 59, 999);
      }

      const emitValue = value ? this.toStringDate(value) : '';
      const dataForWorker = {
        date: emitValue,
        task: this.item,
      };

      CalendarWorker.calculate(dataForWorker, 'getClosestWorkTime').then(time => {
        this.$emit('onChange', time);
      });
      this.pickerOpen = false;
    },

    toStringDate(date) {
      return moment(date, constants.TASK_DATES_FORMAT).toDate();
    },

    setSelected() {
      this.settingSelected = true;
      const $$ui = $$(this.datePickerId);
      const date = this.value;

      $$ui && date && $$ui.selectDate(date);
    },

    async triggerClick() {
      this.pickerOpen = !this.pickerOpen;

      if (this.pickerOpen) {
        await this.$nextTick(this.setSelected);
      }
      if (!this.value) {
        this.settingSelected = false;
      }
    },

    isBlockedDate(date) {
      if (this.property !== 'start_date' && this.property !== 'end_date') return false;

      if (this.property === 'end_date') {
        if (new Date(date).valueOf() < dateHelper.createDayStartedAt00(_.clone(this.item.start_date)).valueOf()) {
          return true;
        }
      }

      if (calendarModule.isWorkDay(date, this.calendarConfig)) return false;

      return true;
    },

    isBlockedTime(date) {
      if (this.property !== 'start_date' && this.property !== 'end_date') return false;

      if (this.property === 'end_date' && calendarModule.isWorkEndTime(date, this.calendarConfig)) {
        return false;
      }

      if (this.property === 'start_date' && calendarModule.isWorkStartTime(date, this.calendarConfig)) {
        return false;
      }

      return true;
    },
  },

};
</script>

<style module src="./cell.less" lang="less" />
